import { memo, useCallback, useEffect, useState } from "react";
import { ActionButton } from "../ReportsModal/FileItem/Actions/ActionButton/ActionButton";
import { useMimeType } from "../../../../hooks/useMimeType";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { useTranslation } from "react-i18next";
import { useFetch } from "@4uhub/lib4uhub";
import {
  getNonLaboratorialExamsPdfByIdList,
  getNonLaboratorialExamsPdfByIdListTasy,
  getAllNonLaboratorialExams,
  getAllNonLaboratorialExamsTasy,
} from "../../../../services/nonLaboratorialExams.service";
import {
  getLaboratorialExamsFile,
  getLaboratorialExamsFileAsTemporary,
} from "../../../../services/laboratorialExams.service";
import { tokenTemporaryService } from "../../../../services/tokenTemorary.service";

type Props = { identifier: number; cdnUrl: boolean };

const translationPath = "components.reportsModal.actions.";

export const ViewReportTable = memo(({ identifier, cdnUrl }: Props) => {
  const { t } = useTranslation();
  const { detectMimeType } = useMimeType();

  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [shouldOpenFile, setShouldOpenFile] = useState(false);

  const isTemporaryAccess = !!tokenTemporaryService.getTemporaryAccessToken();

  const { sendRequest: fetchOrigins, loading: originsLoading } = useFetch(
    isTemporaryAccess
      ? getNonLaboratorialExamsPdfByIdListTasy
      : getNonLaboratorialExamsPdfByIdList,
  );

  const { sendRequest: fetchLabFile, loading: labLoading } = useFetch(
    isTemporaryAccess
      ? getLaboratorialExamsFileAsTemporary
      : getLaboratorialExamsFile,
  );

  const { sendRequest: fetchNonLabFile, loading: nonLabLoading } = useFetch(
    isTemporaryAccess
      ? getAllNonLaboratorialExamsTasy
      : getAllNonLaboratorialExams,
  );

  const isLoading = originsLoading || labLoading || nonLabLoading;

  const openInNewTabHandler = useCallback(async () => {
    const { data: origins, success } = await fetchOrigins(identifier);
    if (!success || !origins || origins.length === 0) {
      setSelectedFile(null);
      return;
    }

    const first = origins[0] as {
      origin: "LAUDO_PACIENTE" | "LAUDO_PACIENTE_PDF" | string;
      sequencialImageControl?: number | null;
      sequencialPdfControl?: number | null;
    };

    if (first.origin === "LAUDO_PACIENTE") {
      const { data, success: okLab } = await fetchLabFile({
        Identifier: identifier,
        ImageIdentifier:
          first.sequencialImageControl && first.sequencialImageControl > 0
            ? first.sequencialImageControl
            : 1,
      });
      setSelectedFile(okLab && data ? data : null);
    } else if (
      first.origin === "LAUDO_PACIENTE_PDF" &&
      first.sequencialPdfControl
    ) {
      const { data, success: okNonLab } = await fetchNonLabFile({
        identifier,
        pdfIdentifier: first.sequencialPdfControl,
      });
      const file =
        okNonLab && Array.isArray(data) && data.length > 0
          ? (data[0]?.medicalCertificateFile as string | null)
          : null;
      setSelectedFile(file ?? null);
    } else {
      setSelectedFile(null);
    }

    setShouldOpenFile(true);
  }, [fetchOrigins, identifier, fetchLabFile, fetchNonLabFile]);

  useEffect(() => {
    if (!shouldOpenFile || !selectedFile) return;
    if (
      cdnUrl &&
      (selectedFile.startsWith("http://") || selectedFile.startsWith("https://"))
    ) {
      window.open(selectedFile, "_blank");
      return;
    }
    const mimeTypeMap: Record<string, string> = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      txt: "text/plain",
      pdf: "application/pdf",
      zip: "application/zip",
    };

    const ext = detectMimeType(selectedFile);
    if (!ext) {
      setShouldOpenFile(false);
      return;
    }

    try {
      const byteCharacters = atob(selectedFile);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: mimeTypeMap[ext.toLowerCase()] || "application/pdf",
      });
      const blobUrl = URL.createObjectURL(blob);
      const newTab = window.open();
      if (newTab) newTab.location.href = blobUrl;
    } finally {
      setShouldOpenFile(false);
    }
  }, [selectedFile, shouldOpenFile, detectMimeType, cdnUrl]);

  return (
    <ActionButton
      icon={OpenInNewRoundedIcon}
      tooltip={t(`${translationPath}open`)}
      disabled={isLoading}
      onClick={openInNewTabHandler}
      loading={isLoading}
    />
  );
});
